import create from "zustand";
import { Vector3 } from "three";
import { devtools, persist } from "zustand/middleware";
import produce from "immer";
import { ItemGroupI, ItemI, OriginDecorCatalog, OriginDecorCategoryI, OriginDecorI, SectionItemI } from "../shared";
import { normalizeWorkbenchToolboxOriginDecor } from "../utils/workbenchToolbox";
import {
    IAlert,
    Label,
    SectionInterface,
    Settings,
    SideType,
    TargetZoomType,
    TireRackItemI,
    TireRackModalInterface,
} from "./types/store";
import { ShelvingStoreInterface, useShelvingStore } from "./useShelvingStore";
import { storeRoomInterface, useStoreRoom } from "./useStoreRoom";
import { getStateForSave } from "./helpers/getStateForSave";
import { API } from "../controllers";
import { getWallLineVertices } from "../components/SceneObjects/ButtonsScene/utils";
import { getCameraPositionToMiddleLine, getTargetPositionToMiddleLine } from "./helpers";
import { ConstructionElement, ConstructionsResponse } from "../controllers/types";
import { updateAllModelUrls } from "../utils/updateModelUrl";
import { updateAllPartsData } from "../utils/updatePartsData";
import { collectProject } from "../utils/collectProject";
import { isNewSite } from "../utils";

export interface TireRackProductI {
    ID: string;
    NAME: string;
    CODE: string;
    ACTIVE: "Y" | "N";
    SORT: number;
    PREVIEW_TEXT: string;
    DETAIL_TEXT: string;
    DATE_CREATE: string;
    TIMESTAMP_X: string;
    PREVIEW_PICTURE?: string;
    DETAIL_PICTURE?: string;
    PRICE?: {
        VALUE: number;
        CURRENCY: string;
        DISCOUNT_VALUE?: number;
        DISCOUNT?: number;
        PERCENT?: number;
    };
    PROPERTIES: Record<
        string,
        {
            ID: string;
            NAME: string;
            VALUE: any;
            DESCRIPTION: string;
            PROPERTY_TYPE: string;
            MULTIPLE: "Y" | "N";
            CODE: string;
        }
    >;
    QUANTITY?: number;
    AVAILABLE?: "Y" | "N";
    CAN_BUY_ZERO?: "Y" | "N";
}

interface sectionEditModalInterface {
    isOpen: boolean;
    section?: SectionInterface | undefined;
    type?: "edit" | "indent" | "fill";
}

interface itemEditModalInterface {
    isOpen: boolean;
    item?: SectionItemI;
    section?: SectionInterface;
}

interface DecorsModalInterface {
    isOpen: boolean;
    category: string | undefined;
}

interface ProjectModalInterface {
    isOpen: boolean;
}

interface SaveProjectModalInterface extends ProjectModalInterface {
    modalBody?: "save" | "saveAs" | "auth";
}

interface SavedProjectModalInterface extends ProjectModalInterface {
    hash?: string;
    isRewrite?: boolean;
}

interface SystemPropertiesInterface {
    name:
        | "projectModal"
        | "sectionFilling"
        | "proMode"
        | "showCeiling"
        | "cameraPosition"
        | "targetCameraPosition"
        | "isViewMode"
        | "isWallsGreen"
        | "targetZoom"
        | "currentSide"
        | "settingsMenuIsVisible"
        | "proModeEverActivated";
    subName?: "isOpen" | "isFilling" | "itemID" | "isHoldButton";
    value: boolean | string | number | Vector3 | TargetZoomType | SideType;
}

interface SystemBooleanPropertiesInterface {
    name: "generalListSpecification";
    value: boolean;
}

interface TireRacksModalInterface {
    isOpen: boolean;
    category: string | undefined;
}

export interface useStoreSystemInterface {
    tireRacksModal: TireRacksModalInterface;
    currentConstructions: ConstructionElement[];
    currentConstructionCategoryName: string;
    tireRacksFromProducts: TireRackProductI[];
    loadingTireRacksFromProducts: boolean;
    proModeEverActivated: boolean;
    currentSide: SideType;
    currentProjectUserId?: number;
    userId?: number;
    location: undefined | string;
    prevLocation: undefined | string;
    sectionEditModal: sectionEditModalInterface;
    itemEditModal: itemEditModalInterface;
    decorsModal: DecorsModalInterface;
    tireRackModal: TireRackModalInterface;
    alerts: IAlert[];
    itemsGroup: ItemGroupI[];
    parts?: ItemI[];
    labels: Label[];
    isMoveCamera: boolean;
    moveCameraEnabled: boolean;
    targetZoom: TargetZoomType;
    currentSection?: SectionInterface;
    currentEditItem: { id: string | undefined; isMove: boolean; refItem: any; position?: Vector3 };
    currentSectionId: string | undefined;
    settings: Settings;
    animationAction: any;
    currentDecorId: string | null;
    currentTireRackId: string | null;
    editSection: null | string;
    isLoading: boolean;
    isPdfPreparation: boolean;
    projectModal: ProjectModalInterface;
    saveProjectModal: SaveProjectModalInterface;
    savedProjectModal: SavedProjectModalInterface;
    originDecors: OriginDecorI[];
    currentOriginDecors: OriginDecorI[];
    currentOriginCategoryName: string;
    originDecorsMap: Record<string, OriginDecorI>;
    originDecorCategory: OriginDecorCategoryI;
    proMode: boolean;
    sectionFilling: {
        isFilling: boolean;
        itemID: string;
        isHoldButton: boolean;
    };
    showCeiling: boolean;
    cameraPosition: Vector3;
    targetCameraPosition: Vector3;
    isViewMode: boolean;
    isWallsGreen: boolean;
    viewCameraPosition: Vector3 | null;
    viewTargetCameraPosition: Vector3 | null;
    settingsMenuIsVisible: boolean;
    generalListSpecification: boolean;
    setTireRacksModal: (isOpen: boolean, category?: string | undefined) => void;
    setProModeEverActivated: (value: boolean) => void;
    setCurrentSide: (side: SideType) => void;
    setLocation: (newLocation: undefined | string) => void;
    setSectionEditModal: (newSectionEditModal: sectionEditModalInterface) => void;
    setItemEditModal: (newItemEditModal: itemEditModalInterface) => void;
    setTireRackModal: (isOpen: boolean, tireRack?: TireRackItemI, mode?: "create" | "edit") => void;
    addAlert: (alert: IAlert) => void;
    deleteAlert: (alertId: number) => void;
    updateItemsGroup: (newItemsGroup: ItemGroupI[]) => void;
    updateLabels: () => void;
    setMoveCameraEnabled: (isEnabled: boolean) => void;
    setUserId: (userId: number) => void;
    setIsMoveCamera: (isMove: boolean) => void;
    setTargetZoom: (targetZoom: TargetZoomType) => void;
    setCurrentSection: (currentSection?: SectionInterface) => void;
    setCurrentEditItem: (id: string | undefined, isMove: boolean, refItem?: any, position?: Vector3) => void;
    setCurrentSectionId: (sectionId?: string | undefined) => void;
    updateSettings: (settings: Settings) => void;
    setAnimationAction: (action: any) => void;
    selectDecor: (currentDecorId: string | null) => void;
    selectTireRack: (currentTireRackId: string | null) => void;
    setDecorsModal: (isOpen: boolean, category: string | undefined) => void;
    setSaveProjectModal: (isOpen: boolean, modalBody?: "save" | "saveAs" | "auth") => void;
    setSavedProjectModal: (isOpen: boolean, hash: string, isRewrite?: boolean) => void;
    setEditSection: (id: string | null) => void;
    setIsLoading: (isLoading: boolean) => void;
    setIsPdfPreparation: (isPdfPreparation: boolean) => void;
    setSystemProperties: (arrProperties: SystemPropertiesInterface[]) => void;
    setWallZoomTarget: () => void;
    setItemZoomTarget: () => void;
    setBooleanSystemProperties: (arrProperties: SystemBooleanPropertiesInterface[]) => void;
    saveProject: (hash?: string, isNewProject?: boolean) => Promise<string | undefined>;
    loadProject: (hash: string) => Promise<string | undefined>;
    forceLoadProject: () => void;
    originDecorsCatalogs: Record<string, OriginDecorCatalog>;
    setOriginDecors: (originDecorsCatalogs: Record<string, OriginDecorCatalog>) => void;
    constructions: ConstructionsResponse;
    setConstructions: (constructions: ConstructionsResponse) => void;
    loadTireRacksFromProducts: (params?: any) => Promise<void>;
    setTireRacksFromProducts: (tireRacks: TireRackProductI[]) => void;
}

// Store для всяких системных не требующих сохранений состояний
// @ts-ignore
export const useStoreSystem = create<
    useStoreSystemInterface,
    [["zustand/devtools", never], ["zustand/persist", Partial<useStoreSystemInterface>]]
>(
    devtools(
        persist(
            (set, get) => ({
                tireRacksModal: {
                    isOpen: false,
                    category: undefined,
                },
                currentConstructions: [],
                currentConstructionCategoryName: "Конструкции",
                tireRacksFromProducts: [],
                loadingTireRacksFromProducts: false,
                proModeEverActivated: false,
                currentSide: "front",
                location: undefined,
                prevLocation: undefined,
                sectionEditModal: {
                    isOpen: false,
                    section: undefined,
                    type: undefined,
                },
                itemEditModal: {
                    isOpen: false,
                    item: undefined,
                },
                decorsModal: {
                    isOpen: false,
                    category: undefined,
                },
                alerts: [],
                itemsGroup: [], //заполнение
                parts: [], //запчасти
                labels: [],
                isMoveCamera: false,
                moveCameraEnabled: true,
                targetZoom: "wall" as TargetZoomType,
                currentSection: undefined,
                currentEditItem: { id: undefined, isMove: false, refItem: null },
                currentSectionId: undefined,
                settings: {
                    isVisibleSizes: true,
                },
                animationAction: null,
                currentDecorId: null,
                editSection: null,
                isLoading: false,
                isPdfPreparation: false,
                projectModal: { isOpen: false },
                saveProjectModal: { isOpen: false },
                savedProjectModal: { isOpen: false },
                originDecors: [],
                currentOriginDecors: [],
                currentOriginCategoryName: "Категория",
                originDecorsMap: {},
                originDecorsCatalogs: {},
                constructions: {},
                originDecorCategory: "",
                proMode: false,
                sectionFilling: {
                    isFilling: false,
                    itemID: "",
                    isHoldButton: false,
                },
                showCeiling: true,
                cameraPosition: new Vector3(0, 0, 0),
                targetCameraPosition: new Vector3(0, 0, 0),
                isViewMode: false,
                isWallsGreen: false,
                viewCameraPosition: null,
                viewTargetCameraPosition: null,
                settingsMenuIsVisible: true,
                generalListSpecification: true,
                tireRackModal: {
                    isOpen: false,
                    tireRack: undefined,
                    mode: "create",
                },
                currentTireRackId: null,
                setTireRacksModal: (isOpen, category) =>
                    set({
                        tireRacksModal: { isOpen, category },
                        currentConstructions: category ? get().constructions[category]?.ELEMENTS || [] : [],
                        currentConstructionCategoryName: category
                            ? get().constructions[category]?.NAME || "Конструкции"
                            : "Конструкции",
                    }),
                setTireRackModal: (isOpen, tireRack, mode = "create") =>
                    set({
                        tireRackModal: { isOpen, tireRack, mode },
                    }),
                selectTireRack: (currentTireRackId) => set({ currentTireRackId }),
                setProModeEverActivated: (value: boolean) => set({ proModeEverActivated: value }),
                setCurrentSide: (side) => {
                    set({ currentSide: side });
                },
                setLocation: (newLocation) => {
                    return set({
                        ...(get().location ? { prevLocation: get().location } : {}),
                        location: newLocation,
                    });
                },
                setSectionEditModal: (newSectionEditModal) => set({ sectionEditModal: newSectionEditModal }),
                setItemEditModal: (newItemEditModal) => set({ itemEditModal: newItemEditModal }),
                addAlert: (alert) => set({ alerts: [...get().alerts, alert] }),
                deleteAlert: (alertId) => set({ alerts: get().alerts.filter((alert) => alert.id !== alertId) }),
                updateItemsGroup: (newItemsGroup) => {
                    set({
                        itemsGroup: newItemsGroup,
                        parts: newItemsGroup.find((group) => group?.NAME?.toLowerCase().includes("запчасти"))?.ELEMENTS,
                    });
                },
                updateLabels: () => {
                    const currentShelf = useShelvingStore.getState().getCurrentShelving();
                    const roomStore = useStoreRoom.getState();
                    const labels: Label[] = [];
                    let roomSize = 0;
                    if (["front", "back"].includes(currentShelf.name)) {
                        roomSize = roomStore.length;
                    }
                    if (["left", "right"].includes(currentShelf.name)) {
                        roomSize = roomStore.depth;
                    }
                    const startPosition = -(roomSize / 2 - currentShelf.sideIndent) / 1000;
                    currentShelf.sections.forEach((section) => {
                        const x = startPosition + (section.offset + section.width / 2) / 1000;
                        const y =
                            (roomStore.height - currentShelf.offsetCeiling - currentShelf.height / 2 + 250) / 1000;
                        const position: number[] = [x, y, 0];
                        labels.push({ value: section.width, position });
                    });
                    set({ labels: labels });
                },
                setMoveCameraEnabled: (isEnabled) => set({ moveCameraEnabled: isEnabled }),
                setIsMoveCamera: (isMove) => set({ isMoveCamera: isMove }),
                setTargetZoom: (targetZoom) => set({ targetZoom: targetZoom }),
                setCurrentSection: (currentSection?: SectionInterface) => {
                    set({ currentSection });
                },
                setUserId: (userId) => set({ userId }),
                setCurrentEditItem: (id, isMove = false, refItem, position = undefined) =>
                    set({
                        currentEditItem: {
                            id,
                            isMove,
                            refItem,
                            position,
                        },
                    }),
                setCurrentSectionId: (sectionId) => {
                    set({ currentSectionId: sectionId });
                },
                updateSettings: (settings) => set((state) => ({ settings: { ...state.settings, ...settings } })),
                setAnimationAction: (action) => set(() => ({ animationAction: action })),
                selectDecor: (currentDecorId) => set({ currentDecorId }),
                setDecorsModal: (isOpen, category) =>
                    set({
                        decorsModal: { isOpen, category },
                        originDecorCategory: category,
                        currentOriginDecors: category ? get().originDecorsCatalogs[category].ELEMENTS : [],
                        currentOriginCategoryName: category ? get().originDecorsCatalogs[category].NAME : "Категория",
                    }),
                setSaveProjectModal: (isOpen, modalBody = "save") =>
                    set({
                        saveProjectModal: { isOpen, modalBody },
                    }),
                setSavedProjectModal: (isOpen, hash, isRewrite) =>
                    set({
                        savedProjectModal: { isOpen, hash, isRewrite },
                    }),
                setEditSection: (id) => set({ editSection: id }),
                setIsLoading: async (isLoading) => {
                    set({ isLoading: isLoading });
                },
                setIsPdfPreparation: async (isPdfPreparation) => {
                    set({ isPdfPreparation: isPdfPreparation });
                },
                setSystemProperties: (arrProperties) => {
                    arrProperties.forEach((property: SystemPropertiesInterface) => {
                        if (property.name === "proMode" && property.value === true) {
                            set({ proModeEverActivated: true });
                        }

                        set(
                            produce((state) => {
                                property.subName
                                    ? (state[property.name][property.subName] = property.value)
                                    : (state[property.name] = property.value);
                            })
                        );
                    });
                },
                setBooleanSystemProperties: (arrProperties) => {
                    arrProperties.forEach((property: SystemBooleanPropertiesInterface) => {
                        set(
                            produce((state) => {
                                state[property.name] = property.value;
                            })
                        );
                    });
                },
                saveProject: async (hash?: string, isNewProject?: boolean): Promise<string | undefined> => {
                    const stateForSave = getStateForSave(useStoreRoom.getState(), useShelvingStore.getState(), get());
                    try {
                        set({ isLoading: true });
                        const { data } = await API.Configurator.saveProject(stateForSave, hash, isNewProject);
                        if (data.status === "ok") {
                            const newHash = data.hash;
                            const currentProjectUserId = data.userId;
                            set({ currentProjectUserId });
                            return newHash;
                        }
                        throw new Error(data.status);
                    } catch (e) {
                        throw new Error("Не удалось сохранить проект");
                    } finally {
                        set({ isLoading: false });
                    }
                },

                loadProject: async (hash) => {
                    set({ isLoading: true });
                    try {
                        const { data } = await API.Configurator.loadProject(hash);
                        if (data.status === "ok") {
                            const {
                                configuratorState: oldConfState,
                                ...project
                            }: DeepPartial<{
                                roomState: storeRoomInterface;
                                shelvingState: ShelvingStoreInterface;
                                configuratorState: useStoreSystemInterface;
                            }> = data.project as DeepPartial<{
                                roomState: storeRoomInterface;
                                shelvingState: ShelvingStoreInterface;
                                configuratorState: useStoreSystemInterface;
                            }>;

                            let shouldEnableProMode = false;

                            if (project.shelvingState?.shelving) {
                                const { left, right, back } = project.shelvingState.shelving;

                                const hasLeftContent = left?.sections && left?.sections?.length > 0;
                                const hasRightContent = right?.sections && right?.sections?.length > 0;
                                const hasBackContent = back?.sections && back?.sections?.length > 0;

                                let hasDecorsOnSideWalls = false;
                                if (project.roomState?.decors) {
                                    hasDecorsOnSideWalls = project.roomState.decors.some(
                                        (decor) =>
                                            decor.side === "left" || decor.side === "right" || decor.side === "back"
                                    );
                                }

                                shouldEnableProMode =
                                    hasLeftContent || hasRightContent || hasBackContent || hasDecorsOnSideWalls;
                            }

                            const projectUserId = Number(data.userId);
                            const { configuratorState: newConfState, ...stateForSave } = getStateForSave(
                                useStoreRoom.getState(),
                                useShelvingStore.getState(),
                                get()
                            );

                            // Обновляем все model ссылки и данные запчастей в проекте используя актуальные данные из itemsGroup
                            const currentItemsGroup = useStoreSystem.getState().itemsGroup;
                            const originDecorsMap = useStoreSystem.getState().originDecorsMap;
                            const constructions = useStoreSystem.getState().constructions;

                            let mapConstructions = {};
                            if (constructions) {
                                Object.values(constructions).forEach((c) => {
                                    c?.ELEMENTS?.forEach((el) => {
                                        if (el?.BITRIX_OFFER_ID) {
                                            // @ts-ignore
                                            mapConstructions[el.BITRIX_OFFER_ID as any] = el;
                                        }
                                    });
                                });
                            }

                            if (currentItemsGroup && currentItemsGroup.length > 0) {
                                // Сначала обновляем URL моделей
                                const updatedProject = updateAllModelUrls(project, currentItemsGroup, {
                                    ...(originDecorsMap || {}),
                                    ...mapConstructions,
                                });
                                // Затем обновляем данные запчастей (цены, названия и т.д.)
                                const fullyUpdatedProject = updateAllPartsData(updatedProject, currentItemsGroup);
                                Object.assign(project, fullyUpdatedProject);
                            }

                            if (projectUserId) {
                                set(
                                    produce((state) => {
                                        state.currentProjectUserId = projectUserId;
                                    })
                                );
                            }

                            const clientProject = JSON.stringify(collectProject(stateForSave));
                            const serverProject = JSON.stringify(collectProject(project));

                            if (clientProject !== serverProject) {
                                const newProject = JSON.stringify(project);
                                localStorage.setItem("TEMP_PROJECT", newProject);
                                set(
                                    produce((state) => {
                                        state.projectModal.isOpen = true;
                                    })
                                );
                            }

                            if (oldConfState) {
                                set((state) => {
                                    const {
                                        proMode,
                                        proModeEverActivated,
                                        currentSide,
                                        settings,
                                        cameraPosition,
                                        targetCameraPosition,
                                        showCeiling,
                                        viewCameraPosition,
                                        viewTargetCameraPosition,
                                    } = oldConfState as useStoreSystemInterface;

                                    return {
                                        ...state,

                                        proMode: shouldEnableProMode || proMode || state.proMode,
                                        proModeEverActivated:
                                            shouldEnableProMode || proModeEverActivated || state.proModeEverActivated,
                                        currentSide: currentSide || state.currentSide,
                                        settings: settings || state.settings,
                                        cameraPosition: cameraPosition?.isVector3
                                            ? new Vector3(cameraPosition.x, cameraPosition.y, cameraPosition.z)
                                            : state.cameraPosition,
                                        targetCameraPosition: targetCameraPosition?.isVector3
                                            ? new Vector3(
                                                  targetCameraPosition.x,
                                                  targetCameraPosition.y,
                                                  targetCameraPosition.z
                                              )
                                            : state.targetCameraPosition,
                                        viewCameraPosition: viewCameraPosition?.isVector3
                                            ? new Vector3(
                                                  viewCameraPosition.x,
                                                  viewCameraPosition.y,
                                                  viewCameraPosition.z
                                              )
                                            : state.viewCameraPosition,
                                        viewTargetCameraPosition: viewTargetCameraPosition?.isVector3
                                            ? new Vector3(
                                                  viewTargetCameraPosition.x,
                                                  viewTargetCameraPosition.y,
                                                  viewTargetCameraPosition.z
                                              )
                                            : state.viewTargetCameraPosition,
                                        showCeiling: showCeiling || state.showCeiling,
                                    };
                                });

                                const viewCameraPosition = get().viewCameraPosition;
                                const viewTargetCameraPosition = get().viewTargetCameraPosition;
                                const pathname = window.location.pathname.split("/");
                                const location = pathname[pathname.length - 1];
                                const lineVertices = getWallLineVertices(get().currentSide);
                                get().setSystemProperties([
                                    {
                                        name: "cameraPosition",
                                        value:
                                            viewCameraPosition && viewTargetCameraPosition && location === "view"
                                                ? viewCameraPosition
                                                : getCameraPositionToMiddleLine(lineVertices),
                                    },
                                    {
                                        name: "targetCameraPosition",
                                        value:
                                            viewCameraPosition && viewTargetCameraPosition && location === "view"
                                                ? viewTargetCameraPosition
                                                : getTargetPositionToMiddleLine(lineVertices),
                                    },
                                    {
                                        name: "isViewMode",
                                        value: !!(
                                            viewCameraPosition &&
                                            viewTargetCameraPosition &&
                                            location === "view"
                                        ),
                                    },
                                ]);
                            } else if (shouldEnableProMode) {
                                set(
                                    produce((state) => {
                                        state.proMode = true;
                                        state.proModeEverActivated = true;
                                    })
                                );
                            }
                        } else {
                            console.warn("Failed to load project - project not found or access denied");
                            return "fail";
                        }
                        return "ok";
                    } catch (e) {
                        console.error(e);
                    } finally {
                        set({ isLoading: false });
                    }
                },
                forceLoadProject: () => {
                    const tempProjectString = localStorage.getItem("TEMP_PROJECT");
                    if (tempProjectString) {
                        const tempProject = JSON.parse(tempProjectString);

                        // Обновляем model ссылки и данные запчастей в временном проекте используя актуальные данные из itemsGroup
                        const currentItemsGroup = get().itemsGroup;
                        const originDecorsMap = get().originDecorsMap;
                        const constructions = useStoreSystem.getState().constructions;

                        let mapConstructions = {};
                        if (constructions) {
                            Object.values(constructions).forEach((c) => {
                                c?.ELEMENTS?.forEach((el) => {
                                    if (el?.BITRIX_OFFER_ID) {
                                        // @ts-ignore
                                        mapConstructions[el.BITRIX_OFFER_ID as any] = el;
                                    }
                                });
                            });
                        }

                        if (currentItemsGroup && currentItemsGroup.length > 0) {
                            // Сначала обновляем URL моделей
                            const updatedTempProject = updateAllModelUrls(tempProject, currentItemsGroup, {
                                ...(originDecorsMap || {}),
                                ...mapConstructions,
                            });
                            // Затем обновляем данные запчастей (цены, названия и т.д.)
                            const fullyUpdatedTempProject = updateAllPartsData(updatedTempProject, currentItemsGroup);
                            Object.assign(tempProject, fullyUpdatedTempProject);
                        }

                        useShelvingStore.getState().loadProject(tempProject.shelvingState);
                        useStoreRoom.getState().loadProject(tempProject.roomState);

                        if (isNewSite()) {
                            const enrichedConstructions: ConstructionsResponse = {};
                            Object.keys(constructions).forEach((sectionKey) => {
                                const section = constructions[sectionKey];
                                enrichedConstructions[sectionKey] = {
                                    ...section,
                                    ELEMENTS: section.ELEMENTS.filter((el) => el.newNomenkl?.length > 0),
                                };
                            });
                            useStoreSystem.getState().setConstructions(enrichedConstructions);
                            useStoreSystem.getState().updateItemsGroup(currentItemsGroup);
                        }
                    }
                },
                setOriginDecors: (originDecorsCatalogs) => {
                    const originDecorsMap: Record<string, OriginDecorI> = {};
                    const allOriginDecors: OriginDecorI[] = [];

                    const normalizedOriginDecorsCatalogs = Object.fromEntries(
                        Object.entries(originDecorsCatalogs).map(([categoryId, catalog]) => [
                            categoryId,
                            {
                                ...catalog,
                                ELEMENTS: catalog.ELEMENTS.map(normalizeWorkbenchToolboxOriginDecor),
                            },
                        ])
                    );

                    Object.values(normalizedOriginDecorsCatalogs).forEach((catalog) => {
                        catalog.ELEMENTS.forEach((decor) => {
                            originDecorsMap[decor.ID] = decor;
                        });
                    });

                    set({
                        originDecorsCatalogs: normalizedOriginDecorsCatalogs,
                        originDecors: allOriginDecors,
                        originDecorsMap,
                    });
                },
                setConstructions: (constructions) => set({ constructions }),
                loadTireRacksFromProducts: async (params = {}) => {
                    set({ loadingTireRacksFromProducts: true });
                    try {
                        const { data } = await API.Configurator.getTireRacksFromProducts(params);
                        set({ tireRacksFromProducts: data });
                    } catch (error) {
                        console.error("Failed to load tire racks from products:", error);
                        set({ tireRacksFromProducts: [] });
                    } finally {
                        set({ loadingTireRacksFromProducts: false });
                    }
                },
                setTireRacksFromProducts: (tireRacks) => set({ tireRacksFromProducts: tireRacks }),
                setWallZoomTarget: () => {
                    const lineVertices = getWallLineVertices(get().currentSide);
                    get().setSystemProperties([
                        { name: "cameraPosition", value: getCameraPositionToMiddleLine(lineVertices) },
                        { name: "targetCameraPosition", value: getTargetPositionToMiddleLine(lineVertices) },
                    ]);
                },
                setItemZoomTarget: () => get().setTargetZoom("item"),
            }),
            {
                version: 10927,
                name: "StoreSystem",
                partialize: (state) => ({
                    proMode: state.proMode,
                    proModeEverActivated: state.proModeEverActivated,
                    cameraPosition: state.cameraPosition,
                    targetCameraPosition: state.targetCameraPosition,
                    viewCameraPosition: state.viewCameraPosition,
                    viewTargetCameraPosition: state.viewTargetCameraPosition,
                }),
            }
        ),
        {
            name: "storeSystem",
            enabled: process.env.NODE_ENV === "development",
        }
    )
);
